import { onScopeDispose, watch, type WatchSource } from 'vue'
import { useCentrifugeStore } from '@/modules/centrifuge/stores/centrifugeStore'
import { createRealtimeRefresh } from '@/modules/centrifuge/services/realtimeRefresh'

/** Only mounted views refresh. A new mount already performs its ordinary GET. */
export function useRealtimeRefresh(refresh: () => Promise<void>, revision?: WatchSource<number>) {
  const realtime = useCentrifugeStore()
  const scheduler = createRealtimeRefresh(refresh, error => {
    console.error('❌ [useRealtimeRefresh] Ошибка при обновлении состояния:', error)
    realtime.logEvent('State refresh failed', error)
  })
  watch(() => realtime.recoveryRevision, (newRev, oldRev) => {
    if (newRev !== oldRev) {
      console.warn(`💥 [useRealtimeRefresh] Сработал recoveryRevision (${oldRev} -> ${newRev})! Запуск инвалидации...`)
      scheduler.invalidate(`recoveryRevision ${newRev}`)
    }
  })
  if (revision) {
    watch(revision, (newRev, oldRev) => {
      if (newRev !== oldRev) {
        console.log(`🔄 [useRealtimeRefresh] Изменилась ревизия сущности (${oldRev} -> ${newRev})`)
        scheduler.invalidate('custom revision')
      }
    })
  }
  const STALE_BACKGROUND_TIMEOUT_MS = 5 * 60 * 1000 // 5 минут
  let hiddenAt: number | null = null

  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      hiddenAt = Date.now()
      return
    }

    if (document.visibilityState === 'visible') {
      const now = Date.now()
      const wasHiddenDuration = hiddenAt ? now - hiddenAt : 0
      hiddenAt = null

      if (!realtime.connected) {
        console.log('👁️ [useRealtimeRefresh] Вкладка стала активной, но сокет отключен. Ожидание реконнекта CF...')
        return
      }

      if (wasHiddenDuration >= STALE_BACKGROUND_TIMEOUT_MS) {
        console.warn(`⏳ [useRealtimeRefresh] Вкладка была в фоне ${Math.round(wasHiddenDuration / 1000)} сек (>= 5 мин). Запуск фонового обновления...`)
        scheduler.invalidate('stale background tab')
      } else {
        console.log(`👁️ [useRealtimeRefresh] Вкладка активна (в фоне ${Math.round(wasHiddenDuration / 1000)} сек, сокет подключен). Догрузка не требуется.`)
      }
    }
  }

  document.addEventListener('visibilitychange', onVisibilityChange)
  onScopeDispose(() => {
    scheduler.stop()
    document.removeEventListener('visibilitychange', onVisibilityChange)
  })
}
